import { Autocomplete, Checkbox, TextField } from '@biq/apex-ui';
import { Controller } from 'react-hook-form';
import { OwnersAndLimitedOwnersData, styles } from './upload-file-helper';
import { Grid, InputAdornment, ListItem, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { TruncateChip } from 'components';
import { Children, ReactNode } from 'react';

interface AutoCompleteOwnersAndLimitedOwnersFieldProps {
    name: 'owners' | 'limitedOwners';
    label: 'Owners' | 'Limited Owners';
    control: any;
    filteredOwnersData: OwnersAndLimitedOwnersData[];
    ownersAndLimitedOwnersData: OwnersAndLimitedOwnersData[];
    fieldValue: string[];
    setValue: any;
}

export const chipStyles = {
    marginLeft: '4px',
    marginRight: '4px',
    height: '32px !important',
    marginTop: '2px',
    marginBottom: '2px',
};

export const AutoCompleteOwnersAndLimitedOwnersField = ({
    name,
    label,
    control,
    filteredOwnersData,
    ownersAndLimitedOwnersData,
    fieldValue,
    setValue,
}: AutoCompleteOwnersAndLimitedOwnersFieldProps): React.JSX.Element => {
    const getOptionLabel = (option: any): any => {
        const foundOption = ownersAndLimitedOwnersData.find(
            (opt) => opt?.id === option.id
        );
        return foundOption ? foundOption.value : '';
    };

    const getTruncateLabel = (value: string): string => {
        return value.length > 25 ? value.slice(0, 25) + '...' : value;
    };

    const onDeleteAutocompleteChip = (item: any): void => {
        if (name === 'limitedOwners') {
            setValue(
                'limitedOwners',
                fieldValue.filter((data: any) => item.id !== data.id)
            );
        } else {
            setValue(
                'owners',
                fieldValue.filter((data: any) => item.id !== data.id)
            );
        }
    };

    const renderChip = (
        item: any,
        getTagProps: any,
        onDelete: Function
    ): React.JSX.Element => {
        return (
            <TruncateChip
                color='primary'
                label={getTruncateLabel(item.header)}
                {...getTagProps({ index: 0 })}
                chipStyles={chipStyles}
                readOnly={false}
                variant='outlined'
                onDelete={(): void => onDelete(item)}
            />
        );
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }): React.JSX.Element => (
                <Autocomplete
                    {...field}
                    options={filteredOwnersData}
                    sx={styles.autoCompleteStyles}
                    disableCloseOnSelect
                    getOptionLabel={getOptionLabel}
                    onChange={(__, newValue): any => {
                        field.onChange(newValue);
                    }}
                    value={fieldValue}
                    renderOption={(props, option, { selected }): any => (
                        <ListItem {...props}>
                            <Grid display={'flex'} flexDirection={'row'}>
                                <Checkbox checked={selected} />
                                <Grid display={'flex'} flexDirection={'column'}>
                                    <Typography
                                        sx={styles.checkboxHeaderStyles}
                                    >
                                        {option.header}
                                    </Typography>
                                    <Typography
                                        sx={styles.checkboxSubtitleStyles}
                                    >
                                        {option.subtitle}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </ListItem>
                    )}
                    multiple={true}
                    renderInput={(params): React.JSX.Element => (
                        <TextField
                            {...params}
                            size='medium'
                            label={label}
                            variant='outlined'
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: [
                                    <InputAdornment
                                        key={'searchIcon'}
                                        position='start'
                                    >
                                        <SearchIcon />
                                    </InputAdornment>,
                                    params.InputProps.startAdornment
                                        ? params.InputProps.startAdornment
                                        : [],
                                ],
                            }}
                        />
                    )}
                    renderTags={(value: any, getTagProps): ReactNode => {
                        return Children.toArray(
                            value.map((item: any) =>
                                renderChip(
                                    item,
                                    getTagProps,
                                    onDeleteAutocompleteChip
                                )
                            )
                        );
                    }}
                />
            )}
        />
    );
};
